(() => {
  const normalizeCollectionSearchText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase(typeof document === 'undefined' ? undefined : document.documentElement.lang || undefined)
      .trim();

  const matchesCollectionSearch = (text, query) => {
    const haystack = normalizeCollectionSearchText(text);
    return normalizeCollectionSearchText(query)
      .split(/\s+/)
      .filter(Boolean)
      .every((term) => haystack.includes(term));
  };

  if (typeof module !== 'undefined') {
    module.exports = { matchesCollectionSearch, normalizeCollectionSearchText };
  }
  if (typeof customElements === 'undefined' || customElements.get('collection-search')) return;

  const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  customElements.define(
    'collection-search',
    class CollectionSearch extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.input = this.querySelector('[data-collection-search-input]');
        this.status = this.querySelector('[data-collection-search-status]');
        this.requestId = 0;
        this.baseCount = null;
        this.onInput = this.onInput.bind(this);
        this.onProductsLoaded = this.onProductsLoaded.bind(this);
        this.onGridRendered = this.onGridRendered.bind(this);
        this.input?.addEventListener('input', this.onInput);
        document.addEventListener('collection:products-loaded', this.onProductsLoaded);
        document.addEventListener('collection:grid-rendered', this.onGridRendered);
      }

      disconnectedCallback() {
        clearTimeout(this.debounceTimer);
        this.input?.removeEventListener('input', this.onInput);
        document.removeEventListener('collection:products-loaded', this.onProductsLoaded);
        document.removeEventListener('collection:grid-rendered', this.onGridRendered);
        this.initialized = false;
      }

      onInput() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.search(), 180);
      }

      onProductsLoaded() {
        if (this.query) this.filterCards(this.query);
      }

      onGridRendered() {
        if (!this.query) return;
        this.baseCount = null;
        setTimeout(() => this.search(), 0);
      }

      get query() {
        return this.input?.value.trim() || '';
      }

      get cards() {
        return [...document.querySelectorAll('[data-product-grid] > [data-product-card]')];
      }

      get countElement() {
        return document.getElementById('ProductCountDesktop');
      }

      async search() {
        const requestId = ++this.requestId;
        const query = this.query;
        if (!query) {
          this.clearSearch();
          return;
        }

        if (this.baseCount === null) this.baseCount = this.countElement?.textContent?.trim() || '';
        document.getElementById('ProductGridContainer')?.classList.add('is-collection-searching');
        this.setAttribute('aria-busy', 'true');
        if (this.status) this.status.textContent = this.dataset.loading || '';
        this.filterCards(query);
        await this.loadAllProducts(requestId);
        if (requestId !== this.requestId) return;
        this.filterCards(query);
        this.removeAttribute('aria-busy');
      }

      async loadAllProducts(requestId) {
        let loaded = 0;
        while (requestId === this.requestId) {
          const infiniteScroll = document.querySelector('collection-infinite-scroll');
          if (!infiniteScroll?.nextUrl || typeof infiniteScroll.loadNextPage !== 'function') return;
          if (infiniteScroll.loading) {
            await wait(80);
            continue;
          }

          const previousUrl = infiniteScroll.nextUrl;
          const didLoad = await infiniteScroll.loadNextPage({ reason: 'search' });
          if (!didLoad && infiniteScroll.nextUrl === previousUrl) return;
          loaded += 1;
          if (loaded >= (Number(infiniteScroll.totalPages) || 25)) return;
        }
      }

      filterCards(query) {
        const cards = this.cards;
        let visible = 0;
        for (const card of cards) {
          const matches = matchesCollectionSearch(card.dataset.searchText, query);
          card.hidden = !matches;
          if (matches) visible += 1;
        }

        const count = this.formatCount(visible);
        const desktopCount = document.getElementById('ProductCountDesktop');
        const mobileCount = document.getElementById('ProductCount');
        if (desktopCount) desktopCount.textContent = count;
        if (mobileCount) mobileCount.textContent = count;
        if (this.status) this.status.textContent = count;

        const empty = document.querySelector('[data-collection-search-empty]');
        if (empty) {
          empty.hidden = visible !== 0;
          empty.textContent = (this.dataset.noResults || '').replace('__terms__', query);
        }
      }

      clearSearch() {
        ++this.requestId;
        for (const card of this.cards) card.hidden = false;
        const empty = document.querySelector('[data-collection-search-empty]');
        if (empty) empty.hidden = true;
        const desktopCount = document.getElementById('ProductCountDesktop');
        const mobileCount = document.getElementById('ProductCount');
        if (this.baseCount !== null) {
          if (desktopCount) desktopCount.textContent = this.baseCount;
          if (mobileCount) mobileCount.textContent = this.baseCount;
        }
        this.baseCount = null;
        this.removeAttribute('aria-busy');
        document.getElementById('ProductGridContainer')?.classList.remove('is-collection-searching');
        if (this.status) this.status.textContent = '';
      }

      formatCount(count) {
        const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
        return (template || String(count)).replace(count === 1 ? '1' : '999999', String(count));
      }
    },
  );
})();
